import { useId, useState, type ReactNode } from 'react';
import { Box, Button, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { OfferView, TenantDashboard } from '@/api/generated';
import { ApiErrorState } from '@/components/ApiErrorState';
import { moneyIn } from '@/components/moneyIn';
import { formatCount, formatSignedCount } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { PeriodPicker } from '@/components/PeriodPicker';
import { ReportScope } from '@/components/ReportScope';
import type { PeriodRange } from '@/i18n/periods';
import { useTenantCurrency, useTenantLink } from '@/tenant/useTenant';
import { resolveTokens } from '@/theme';
import { KpiTile } from './KpiTile';
import { WeeklyBars } from './WeeklyBars';
import { useDashboard, type OffersPart } from './useDashboard';

function shownCount(n: number, locale: string): string {
  return formatCount(n, locale);
}

function offerById(offers: OfferView[], id: number): OfferView | undefined {
  return offers.find((offer) => offer.id === id);
}

export function DashboardScreen() {
  const { t } = useTranslation();
  const locale = useLocale();
  const currency = useTenantCurrency();
  const tokens = resolveTokens(useTheme());
  const [range, setRange] = useState<PeriodRange>({});
  const { status, data, error, reload, offers: offersPart } = useDashboard(range);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2.5,
        }}
      >
        {/* The overview carries no from / to: the requested range, or the preset's label. */}
        <ReportScope from={range.from} to={range.to} />
        <PeriodPicker screen="dashboard" scope="tenant" onChange={setRange} />
      </Box>
      {status === 'loading' ? <DashboardSkeleton /> : null}
      {status === 'error' ? <ApiErrorState what={t('errors.what.businessOverview')} error={error} onRetry={reload} /> : null}
      {status === 'ready' && data ? (
        <DashboardReady
          dashboard={data.dashboard}
          offers={data.offers?.offers ?? []}
          offersPart={offersPart}
          currency={currency}
          locale={locale}
          tokens={tokens}
        />
      ) : null}
    </Box>
  );
}

function DashboardSkeleton() {
  const { t } = useTranslation();
  return (
    <Box role="status" aria-label={t('common.loading')}>
      <Grid container spacing={2}>
        {Array.from({ length: 6 }, (_, i) => (
          <Grid key={i} size={{ xs: 6, md: 4, lg: 2 }}>
            <Skeleton variant="rounded" height={112} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" height={190} sx={{ mt: 2 }} />
    </Box>
  );
}

function DashboardReady({
  dashboard,
  offers,
  offersPart,
  currency,
  locale,
  tokens,
}: {
  dashboard: TenantDashboard;
  offers: OfferView[];
  offersPart: OffersPart;
  currency: string;
  locale: string;
  tokens: ReturnType<typeof resolveTokens>;
}) {
  const { t } = useTranslation();
  const subs = dashboard.subscribers;
  const guests = dashboard.guests;
  const dash = t('statements.unavailable');
  const money = (minor: number) => moneyIn(minor, currency, locale, dash);
  const revenue = dashboard.revenue;
  const newInPeriod = subs.newInPeriod;
  const churnedInPeriod = subs.churnedInPeriod;
  const retailMinor = revenue.retailMinor;
  const wholesaleMinor = revenue.wholesaleMinor;
  const upliftMinor = revenue.upliftMinor;
  const wholesaleRatio = retailMinor > 0 ? wholesaleMinor / retailMinor : 0;
  const link = useTenantLink();
  const byOfferId = useId();
  const promotionsId = useId();
  const activeNote = t('dashboard.kpi.activeSplit', {
    monthly: shownCount(subs.activeMonthly, locale),
    annual: shownCount(subs.activeAnnual, locale),
  });
  const annualPrepaidMinor = revenue.annualPrepaidMinor;
  const net = formatSignedCount(newInPeriod - churnedInPeriod, locale);
  const retailNote = t('dashboard.kpi.retailNote', {
    wholesale: money(wholesaleMinor),
    uplift: money(upliftMinor),
  });
  const retailTileNote =
    annualPrepaidMinor > 0
      ? `${retailNote} · ${t('dashboard.kpi.annualPrepaid', {
          amount: money(annualPrepaidMinor),
        })}`
      : retailNote;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
          <KpiTile
            label={t('dashboard.kpi.active', { period: t('dashboard.kpi.now') })}
            value={shownCount(subs.active, locale)}
            note={activeNote}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
          <KpiTile
            label={t('dashboard.kpi.trialing', { period: t('dashboard.kpi.now') })}
            value={shownCount(subs.trialing, locale)}
            note={t('dashboard.kpi.trialingNote')}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
          <KpiTile
            label={t('dashboard.kpi.newChurned', { period: t('dashboard.kpi.inPeriod') })}
            value={`${shownCount(newInPeriod, locale)} / ${shownCount(churnedInPeriod, locale)}`}
            note={t('dashboard.kpi.net', { n: net })}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
          <KpiTile
            label={t('dashboard.kpi.seatsConnected', { period: t('dashboard.kpi.now') })}
            value={t('dashboard.kpi.seatsConnectedValue', {
              seats: shownCount(guests.chargedSeats, locale),
              connected: shownCount(dashboard.connectedAccounts, locale),
            })}
            note={t('dashboard.kpi.seatsNote')}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
          <KpiTile
            label={t('dashboard.kpi.retail', { period: t('dashboard.kpi.inPeriod') })}
            value={money(retailMinor)}
            note={retailTileNote}
            emphasis
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2 }}>
          <KpiTile
            label={t('dashboard.kpi.uplift', { period: t('dashboard.kpi.inPeriod') })}
            value={money(upliftMinor)}
            note={t('dashboard.kpi.upliftNote')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <WeeklyBars
            series={dashboard.series}
            wholesaleRatio={wholesaleRatio}
            currency={currency}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={2}>
            <MiniCard title={t('dashboard.byOffer')} headingId={byOfferId}>
              {offersPart.status === 'error' ? (
                <Box sx={{ p: 2 }}>
                  <ApiErrorState error={offersPart.error} onRetry={offersPart.reload} />
                </Box>
              ) : null}
              {offersPart.status === 'error' || dashboard.offers.length === 0 ? null : (
                <MiniList labelledBy={byOfferId}>
                  {dashboard.offers.map((row) => {
                    const offer = offerById(offers, row.retailOfferId);
                    const period =
                      offer?.interval === 'YEAR' ? t('dashboard.perYear') : t('dashboard.perMonth');
                    const price = offer === undefined ? undefined : money(offer.baseRetailMinor);
                    const label =
                      price === undefined
                        ? `#${row.retailOfferId}`
                        : price === dash
                          ? dash
                          : `${price} ${period}`;
                    const current = offer?.current === true ? ` ${t('dashboard.current')}` : '';
                    return (
                      <MiniItem key={String(row.retailOfferId)}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                          {label}
                          {current}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {shownCount(row.subscribers, locale)}
                        </Typography>
                      </MiniItem>
                    );
                  })}
                </MiniList>
              )}
            </MiniCard>

            <MiniCard title={t('dashboard.promotions')} headingId={promotionsId}>
              {dashboard.promotions.length === 0 ? (
                <Typography variant="body2" sx={{ color: tokens.text2, px: 2.25, py: 1.5 }}>
                  {t('dashboard.noPromotions')}
                </Typography>
              ) : (
                <MiniList labelledBy={promotionsId}>
                  {dashboard.promotions.map((promo) => (
                    <MiniItem key={promo.code}>
                      <Typography variant="mono" sx={{ flex: 1, minWidth: 0 }}>
                        {promo.code}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {shownCount(promo.redemptionsInPeriod, locale)}
                      </Typography>
                    </MiniItem>
                  ))}
                </MiniList>
              )}
              <Button
                component={RouterLink}
                to={link('promotions')}
                sx={{ alignSelf: 'flex-start', px: 2.25, py: 1.5 }}
              >
                {t('dashboard.managePromotions')}
              </Button>
            </MiniCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

function MiniCard({
  title,
  headingId,
  children,
}: {
  title: string;
  headingId: string;
  children: ReactNode;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Paper sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="h3"
        component="h2"
        id={headingId}
        sx={{
          px: 2.25,
          py: 1.75,
          borderBottom: '1px solid',
          borderColor: tokens.line,
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/**
 * A short named list of label / count rows (TMMRSL-0072): real list semantics, named by its card's
 * heading; it looks as the rows always did.
 */
function MiniList({ labelledBy, children }: { labelledBy: string; children: ReactNode }) {
  return (
    <Box component="ul" aria-labelledby={labelledBy} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Box>
  );
}

function MiniItem({ children }: { children: ReactNode }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.25,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: tokens.line,
      }}
    >
      {children}
    </Box>
  );
}
